import * as angular from "angular"
import { FieldViewModel, INtrReqNutrients, isArray, isUndefinedOrNull, RegionId, sum } from "../../omnia"
import { roundRequirement } from "../../services/rounding"
import { getSnsIndexFromAmount } from "../../services/sns"
import { IBalanceUpdateModel, INtrReq, IReqZoneUpdateModel, ISoilAnalysis, NutrientGroup, NutrientType } from "../../types"
import { CroppingAttrs } from "./cropping-attrs"
import { getRequirementDetails } from "./details"
import { getNutrientDefs, NutrientDef } from "./nutrient-def"
import { NutrientViewModel, NutritentChangeType } from "./nutrient-vm"
import { NitrogenSnsMethod } from "@omnia/shared/generated/enums"
import { isNumber } from "@omnia/shared/utils/utils"
import { getGaugeStyle, getValueIndex } from "@omnia/shared/soil/colors/olsen"

const nitrogenBreakevenDefaultComment = "Nitrogen requirement adjusted for breakeven ratio"

export class ReqZoneVM {

    static createZones(
        context: {
            region: RegionId,
            soilAnalysis: ISoilAnalysis[],
            watcher: (zone: ReqZoneVM, nutrient: NutrientViewModel) => void
        },
        cropping: FieldViewModel,
        req: INtrReq[]): ReqZoneVM[] {

        req = req.filter(r => (r.Zone && r.Geometry) || (r.NutrientGroup == NutrientGroup.AllNutrients))

        let vm = req.map(r => {
            let sa = context.soilAnalysis.find(sa => sa.SoilAnalysisId == r.SoilAnalysisId)
            return new ReqZoneVM(cropping, context.region, r, sa, context.watcher)
        })

        let wholeFieldVm = vm.find(z => !z.req.Zone)

        if (wholeFieldVm) {
            let zonesVm = vm.filter(z => z != wholeFieldVm)
            wholeFieldVm.setupWholeField(zonesVm)
            zonesVm.forEach(z => z.setupZone(wholeFieldVm))
        }

        vm = vm.sort((r1, r2) => {
            let diff = (r1.wholeField ? 0 : 1) - (r2.wholeField ? 0 : 1)
            if (!diff) diff = r1.zoneNumber - r2.zoneNumber
            return diff
        })

        return vm

    }

    public wholeField: boolean

    public zone: boolean
    public zoneNumber: number
    public area: number

    public description: string

    public nutrients: NutrientViewModel[]
    public nutrientsIndex: { [key: string]: NutrientViewModel }

    public croppingAttrs: CroppingAttrs

    public comments: string
    public showComments: boolean

    private storedSoilAnalysis: {
        P2O5?: number
        K2O?: number
        MgO?: number
        pH?: number
    }

    private _field: ReqZoneVM
    private _zones: ReqZoneVM[]

    private _details: { key: string, value: any }[]

    private _singleWholeFieldZone: ReqZoneVM
    private _hidden: boolean


    constructor(public cropping: FieldViewModel, private region: RegionId, public req: INtrReq, private soilAnalysis: ISoilAnalysis, private watcher: (zone: ReqZoneVM, nutrient: NutrientViewModel) => void) {
        this.req = angular.extend({}, req)
        this.watcher = watcher || (() => { })
    }

    private setupWholeField(zones: ReqZoneVM[]) {
        this._zones = zones
        this.setup(true)
    }

    private setupZone(field: ReqZoneVM) {
        this.wholeField = false
        this._field = field
        this.setup(false)
    }

    private setup(wholeField: boolean) {

        let req = this.req

        this.wholeField = wholeField
        this.zone = req.Zone
        this.zoneNumber = req.ZoneNumber
        this.area = Math.round(req.Area / 100) / 100

        if (this.wholeField) {
            this.description = "Field requirements"
            this.setupSingleWholeFieldZone()
        }
        else {
            this.description = `Zone ${this.zoneNumber}`
        }

        this.description += ` (${this.area} ha)`

        this.nutrients = getNutrientDefs().map(def => {

            if (this.checkNutrient(def)) {
                let edit = this.canEditNutrient(def)
                return new NutrientViewModel(def, this.req, edit, (n, t) => this.onNutrientChanged(n, t))
            }

        }).filter(i => !!i)

        this.nutrientsIndex = this.nutrients.reduce((index, ntr) => {
            index[ntr.key] = ntr
            return index
        }, {})

        this.storedSoilAnalysis = {
            P2O5: req.PkAnalysisP2O5,
            K2O: req.PkAnalysisK2O,
            MgO: req.PkAnalysisMgO,
            pH: req.SoilAnalysispH
        }

        this.comments = req.Comments
        this.showComments = !!this.comments

        this.croppingAttrs = new CroppingAttrs(this.req, this.cropping.cropping, this.region)

    }

    private setupSingleWholeFieldZone() {

        if (this.wholeField && (this._zones.length == 1) && (this._zones[0].nutrientGroup == this.nutrientGroup)) {

            this._singleWholeFieldZone = this._zones[0]
            this._singleWholeFieldZone._hidden = true

        }

    }

    get id(): number {
        return this.req.NtrReqId
    }

    private canEditNutrient(def: NutrientDef): boolean {
        if (!this._zones) return !this._hidden
        if (this.wholeField && (!!this._singleWholeFieldZone)) return true
        return (this._zones.filter(z => z.checkNutrient(def)).length == 0)
    }

    get croppingId(): number {
        return this.cropping.croppingId
    }

    get nutrientGroup() {
        return this.req.NutrientGroup
    }

    get nutrient() {
        return this.req.Nutrient
    }

    get hidden(): boolean {
        return this._hidden
    }

    get hasSingleWholeFieldZone(): boolean {
        return !!this._singleWholeFieldZone
    }

    get soilAnalysisNumber(): string {
        if (this.soilAnalysis) {
            return this.soilAnalysis.AnalysisNumber
        }
    }

    get targetYield(): number {
        if (this.croppingAttrs) {
            return this.croppingAttrs.targetYield
        }
    }

    set targetYield(value: number) {
        this.croppingAttrs.targetYield = value
    }

    get defaultTargetpH(): number {
        return this.req.LimeCropTargetpHDefault || this.req.LimeCropTargetpH
    }

    get croppingAttrsEditContext() {
        return this.croppingAttrs?.croppingAttrsEditContext
    }

    public hasChanged(): boolean {
        if (this.nutrients && this.nutrients.some(n => n.hasChanged())) return true
        if (this.comments != this.req.Comments) return true
        if (this.haveCalculationParametersChanged()) return true
        return false
    }

    haveCalculationParametersChanged(): boolean {
        return !!this.getCalculationParametersChanges()
    }

    getCalculationParametersChanges(): { [key: string]: any } {
        return this.croppingAttrs.getCalculationParametersChanges()
    }

    getNutrient(type: NutrientType): NutrientViewModel {
        return this.nutrients.find(n => n.nutrientType == type)
    }

    public getNutrients(filter?: (ntr: NutrientViewModel) => boolean, target: any = {}, provider?: (NutrientViewModel) => number): INtrReqNutrients {

        filter = filter || (ntr => true)
        provider = provider || (n => n.value)

        let model = <any>this.nutrients.filter(n => filter(n)).reduce((nutrients, n) => {
            nutrients["Required" + n.key] = provider(n)
            return nutrients
        }, target)

        return model

    }

    public getUpdateModel(): IReqZoneUpdateModel {

        let model = <IReqZoneUpdateModel>{
            NtrReqId: this.req.NtrReqId,
            Comments: this.comments
        }

        this.getNutrients(ntr => ntr.isCustom(), model, n => n.getEffectiveValue())

        model.Balance = this.getBalanceUpdateModel()

        return model

    }

    private getBalanceUpdateModel(): IBalanceUpdateModel {

        let model = this.nutrients
            .filter(n => n.hasOpeningBalance && n.hasCustomBalance())
            .reduce((model, ntr) => {
                model[ntr.key] = ntr.openingBalance
                return model
            }, {})

        return <IBalanceUpdateModel>model

    }

    private checkNutrient(def: NutrientDef) {

        const ntr = this.req.Nutrient

        if (ntr) {
            return def.nutrientType == ntr
        }

        let value = Math.pow(2, def.nutrientType - 1)
        return this.req.NutrientGroup & value
    }

    getRequirement(key: string): number {
        if (this.nutrientsIndex) {
            let ntr = this.nutrientsIndex[key]
            if (ntr) {
                return ntr.value
            }
        }
    }

    cropSupportsYield(): boolean {
        let crop = this.cropping.crop
        return crop && crop.TargetYieldReqd
    }

    hasAnalysis(ntr: NutrientViewModel) {

        let key = ntr.analysisAttribute

        if (key == "N") {
            return this.hasNitrogenAnalysis()
        }

        return this.hasSoilAnalysis(key)

    }

    private hasNitrogenAnalysis(): boolean {

        let req = this.req

        if (req) {

            switch (req.SnsMethod) {
                case NitrogenSnsMethod.NMIN:
                case NitrogenSnsMethod.SMN:
                    return true
            }
        }

    }

    private hasSoilAnalysis(key: string): boolean {

        var sa = this.storedSoilAnalysis
        if (sa) {
            if (key) return isNumber(sa[key])
            return true
        }

    }

    getAnalysisValue(nutrient: NutrientViewModel, index?: boolean): number {

        let key = nutrient.analysisAttribute

        let value: number

        if (key == "N") {
            value = this.getNitrogenAnalysisValue(index)
        }
        else {
            value = this.getSoilAnalysisValue(key, index)
        }

        let rounding = nutrient.rounding

        if (rounding && (!isUndefinedOrNull(value))) {

            if (key != "Lime") {
                let d = (index ? 10 : rounding)
                value = Math.round(value * d) / d
            }

        }

        return value

    }

    getNitrogenAnalysisValue(index?: boolean): number {

        let req = this.req

        if (req) {

            var sns = req.Sns

            if (this.hasNitrogenAnalysis() && (!index)) {
                return sns
            }

            return getSnsIndexFromAmount(sns)

        }

    }

    getSoilAnalysisValue(key: string, index?: boolean): number {

        var sa = this.storedSoilAnalysis

        if (sa) {

            var value = sa[key]

            if (index && (key != "pH")) {
                value = getValueIndex(value, key)
            }

            return value

        }

    }

    getAnalysisStyle(nutrient: NutrientViewModel): any {

        let key = nutrient.analysisAttribute

        if ((key != "N") || this.hasNitrogenAnalysis()) {

            var value = this.getAnalysisValue(nutrient, false)

            let style = getGaugeStyle(value, key)

            return style

        }

    }

    private onNutrientChanged(ntr: NutrientViewModel, change?: NutritentChangeType) {

        if (this._field) {
            let fieldNutrient = this._field.getNutrient(ntr.nutrientType)
            this._field.onNutrientChanged(fieldNutrient, change)
        }
        else if (this.wholeField && this._zones && (!ntr.canEdit())) {
            this.calculateAvgRequirement(ntr)
        }

        if ((ntr.nutrientType === NutrientType.N)
            && (change === "resetToCalculated")
            && (this.comments === nitrogenBreakevenDefaultComment)) {
            this.comments = undefined
        }

        this.watcher(this, ntr)

    }

    private calculateAvgRequirement(ntr: NutrientViewModel) {

        let requirements = this._zones.map(z => {
            return {
                nutrient: z.getNutrient(ntr.nutrientType),
                area: z.req.Area,
                changed: z.hasChanged(),
            }
        }).filter(r => r.nutrient)

        if (requirements.every(r => !r.changed)) {
            ntr.resetToInitial(false)
            return
        }

        let totalArea = sum(requirements, z => z.area)

        if (!totalArea) {
            return 0
        }

        let avg = sum(requirements, r => r.area * (r.nutrient.value || 0)) / totalArea

        avg = roundRequirement(avg, ntr.nutrientType)

        ntr.setValue(avg, false)

    }


    getDetails(): { key: string, value: any }[] {

        if (!isArray(this._details)) {
            this._details = getRequirementDetails(this.req, null)
        }

        return this._details

    }

}
